import React, { useEffect, useState } from "react";
import { Calendar, MapPin, Share2, Check, Users, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n";
import { LoadError } from "@/components/LoadError";
import { Button } from "@/components/ui/button";

export default function Events() {
  const { user, loginWithGoogle } = useAuth();
  const { t } = useI18n();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(null);

  const load = () => {
    setLoading(true);
    setError(false);
    api("/events").then(setEvents).catch(() => setError(true)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const rsvp = async (ev) => {
    if (!user) return loginWithGoogle();
    setBusy(ev.id);
    try {
      const r = await api(`/events/${ev.id}/rsvp`, { method: "POST" });
      setEvents((list) => list.map((e) => (e.id === ev.id ? { ...e, my_rsvp: r.going, rsvp_count: r.rsvp_count } : e)));
    } catch (e) {
      toast.error(e.message || "Failed");
    } finally {
      setBusy(null);
    }
  };

  const share = async (ev) => {
    const text = `${ev.title}\n📍 ${ev.venue}${ev.time_str ? ` • ${ev.time_str}` : ""}`;
    if (navigator.share) {
      try { await navigator.share({ title: ev.title, text }); return; } catch (e) { console.debug("Native share dismissed", e); }
    }
    try { await navigator.clipboard.writeText(text); toast.success(t("inviteCopied")); } catch (e) { console.error("Clipboard copy failed", e); }
  };

  if (loading) return <div className="flex min-h-[50vh] items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-brand" /></div>;
  if (error) return <LoadError onRetry={load} />;

  return (
    <div className="mx-auto max-w-4xl px-4 py-10">
      <h1 className="text-2xl font-black text-ink">{t("eventsTitle")}</h1>

      {events.length === 0 ? (
        <div className="py-24 text-center text-ink-muted">{t("eventsEmpty")}</div>
      ) : (
        <div className="mt-6 space-y-5">
          {events.map((ev) => {
            const d = new Date(ev.date);
            return (
              <div key={ev.id} data-testid={`event-${ev.id}`} className="overflow-hidden rounded-3xl border border-line bg-white sm:flex">
                {ev.image && <img src={ev.image} alt="" className="h-48 w-full object-cover sm:h-auto sm:w-56" />}
                <div className="flex flex-1 flex-col p-5">
                  <div className="flex items-start gap-3">
                    <div className="flex h-14 w-14 flex-col items-center justify-center rounded-xl bg-brand text-white">
                      <span className="text-lg font-black leading-none">{d.toLocaleDateString("en-IN", { day: "2-digit" })}</span>
                      <span className="text-[10px] font-bold uppercase">{d.toLocaleDateString("en-IN", { month: "short" })}</span>
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-ink">{ev.title}</h3>
                      <div className="mt-1 flex items-center gap-1 text-sm text-ink-muted"><MapPin size={13} /> {ev.venue}{ev.time_str ? ` • ${ev.time_str}` : ""}</div>
                      <div className="mt-1 flex items-center gap-1 text-xs font-semibold text-brand"><Users size={12} /> {ev.rsvp_count} {t("attending")}</div>
                    </div>
                  </div>
                  <div className="mt-4 flex gap-3">
                    <Button
                      data-testid={`rsvp-${ev.id}`}
                      onClick={() => rsvp(ev)}
                      disabled={busy === ev.id}
                      className={`h-10 flex-1 rounded-full font-bold ${ev.my_rsvp ? "bg-[#2E7D32] hover:bg-[#256628]" : "bg-brand hover:bg-brand-dark"}`}
                    >
                      {busy === ev.id ? <Loader2 className="animate-spin" /> : ev.my_rsvp ? <><Check size={16} className="mr-1" /> {t("rsvpGoing")}</> : t("rsvpGo")}
                    </Button>
                    <Button variant="outline" onClick={() => share(ev)} data-testid={`share-event-${ev.id}`} className="h-10 rounded-full font-bold">
                      <Share2 size={16} />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
